import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const ACTION_COLORS = { CREATE: '#059669', UPDATE: '#d97706', DELETE: '#dc2626' };

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [tableFilter, setTableFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const limit = 50;

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (tableFilter) params.set('table_name', tableFilter);
      const { data } = await api.get(`/audit-log?${params}`);
      setRows(data.rows);
      setTotal(data.total);
    } catch {}
  }, [page, tableFilter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Audit Trail</h1>
        <select value={tableFilter} onChange={e => { setTableFilter(e.target.value); setPage(1); }} style={{ padding: '6px 12px', border: '1px solid var(--glass-border)', borderRadius: 6, fontSize: 13 }}>
          <option value="">All tables</option>
          {['shifts', 'employees', 'time_entries', 'users', 'workspace_settings'].map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      <div style={{ background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', borderRadius: 10, border: '1px solid var(--glass-border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--hairline)' }}>
              {['Time', 'User', 'Action', 'Table', 'Record', 'Details'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-2)', borderBottom: '1px solid var(--glass-border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <React.Fragment key={r.id}>
                <tr style={{ borderBottom: '1px solid var(--hairline)', cursor: 'pointer' }} onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                  <td style={td}>{new Date(r.created_at).toLocaleString()}</td>
                  <td style={td}>{r.user_id ? r.user_id.slice(0, 8) + '…' : '—'}</td>
                  <td style={td}>
                    <span style={{ background: ACTION_COLORS[r.action] + '20', color: ACTION_COLORS[r.action], padding: '2px 8px', borderRadius: 12, fontWeight: 600, fontSize: 11 }}>
                      {r.action}
                    </span>
                  </td>
                  <td style={td}>{r.table_name}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.record_id?.slice(0, 12)}…</td>
                  <td style={{ ...td, color: '#4f46e5' }}>{expanded === r.id ? '▲ Hide' : '▼ Show'}</td>
                </tr>
                {expanded === r.id && (
                  <tr style={{ background: 'var(--hairline)' }}>
                    <td colSpan={6} style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 24 }}>
                        {r.old_data && (
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 11, marginBottom: 6 }}>BEFORE</div>
                            <pre style={{ margin: 0, fontSize: 11, color: 'var(--text-2)', whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(JSON.parse(r.old_data), null, 2)}
                            </pre>
                          </div>
                        )}
                        {r.new_data && (
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 11, marginBottom: 6 }}>AFTER</div>
                            <pre style={{ margin: 0, fontSize: 11, color: 'var(--text-2)', whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(JSON.parse(r.new_data), null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>No audit entries yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtn}>← Prev</button>
          <span style={{ padding: '6px 12px', fontSize: 13, color: 'var(--text-2)' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtn}>Next →</button>
        </div>
      )}
    </div>
  );
}

const td = { padding: '10px 14px', color: 'var(--text-2)' };
const pageBtn = { padding: '6px 14px', border: '1px solid var(--glass-border)', borderRadius: 6, background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', cursor: 'pointer', fontSize: 13 };
